import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Center, Spinner, Image, Button,
  Flex, Box, Text, IconButton,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb,
} from "@chakra-ui/react";
import { FiDownload, FiRotateCcw } from "react-icons/fi";

const MIN_ZOOM  = 50;
const MAX_ZOOM  = 200;
const ZOOM_STEP = 25;
export function ZoomImageModal({
  isOpen,
  onClose,
  imageUrl,
  isLoading = false,
  title = "Image",
  onDownload,
  size = "2xl",
  headerBg,
  headerColor,
}) {
  const [zoom, setZoom] = useState(75);

  useEffect(() => {
    if (isOpen) setZoom(75);
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={size} isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent maxW="70vw">
        <ModalHeader bg={headerBg} color={headerColor} borderRadius="md">
          {title}
        </ModalHeader>
        <ModalCloseButton color={headerBg ? headerColor : undefined} />

        <ModalBody pb={4}>
          {isLoading ? (
            <Center h="350px">
              <Spinner size="xl" color="red.500" thickness="4px" />
            </Center>
          ) : imageUrl ? (
            <>
             
              <Box
                overflowX="hidden"
                overflowY="auto"
                h="60vh"
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.100"
                display="flex"
              >
                <Box
                  flexShrink={0}
                  style={{
                    width:   "100%",
                    height:  `${(70 * zoom / 100).toFixed(1)}vh`,
                    margin:  "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Image
                    src={imageUrl}
                    alt={title}
                    style={{
                      maxWidth:     "100%",
                      maxHeight:    "100%",
                      objectFit:    "contain",
                      display:      "block",
                      borderRadius: "6px",
                    }}
                  />
                </Box>
              </Box>

              <Flex align="center" gap={3} mt={3} px={1}>
                <IconButton
                  aria-label="Zoom out"
                  icon={<Text fontSize="lg" fontWeight="bold" lineHeight={1}>−</Text>}
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  borderRadius="full"
                  onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
                  isDisabled={zoom <= MIN_ZOOM}
                />
                <Slider
                  value={zoom}
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={ZOOM_STEP}
                  onChange={setZoom}
                  flex={1}
                  focusThumbOnChange={false}
                >
                  <SliderTrack bg="gray.200" h="4px" borderRadius="full">
                    <SliderFilledTrack bg="red.400" />
                  </SliderTrack>
                  <SliderThumb
                    boxSize={4}
                    bg="red.500"
                    boxShadow="md"
                    _focus={{ boxShadow: "0 0 0 3px rgba(199, 33, 39, 0.35)" }}
                  />
                </Slider>
                <IconButton
                  aria-label="Zoom in"
                  icon={<Text fontSize="lg" fontWeight="bold" lineHeight={1}>+</Text>}
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  borderRadius="full"
                  onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
                  isDisabled={zoom >= MAX_ZOOM}
                />
                <Text fontSize="sm" fontWeight="bold" color="gray.600" minW="44px" textAlign="right">
                  {zoom}%
                </Text>
                <IconButton
                  aria-label="Reset zoom"
                  icon={<FiRotateCcw />}
                  size="lg"
                  variant="ghost"
                  colorScheme="gray"
                  borderRadius="full"
                  onClick={() => setZoom(75)}
                  isDisabled={zoom === 75}
                  title="Reset to original"
                />
              </Flex>
            </>
          ) : (
            <Center h="250px" color="gray.500">
              No image available
            </Center>
          )}
        </ModalBody>

        {onDownload && (
          <ModalFooter>
            <Button
              leftIcon={<FiDownload />}
              colorScheme="red"
              variant="outline"
              isDisabled={!imageUrl}
              onClick={() => onDownload(imageUrl)}
            >
              Download
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}